import { Fragment } from 'react';
import { DashRegistrationStep, DonorDashboardStep, historyLinkMap, screeningLinkMap } from '@/types/dashboard';
import type { IJourneyStep } from '@views/register/donor-dashboard-sections/step-circle';
import type { TLocalize } from '@hooks/use-localize';
import type { IDashboardModel } from '@/types/dashboard';
import StepCircle from '@views/register/donor-dashboard-sections/step-circle';

interface IProps {
    model: IDashboardModel;
    t: TLocalize;
    activeStep: DonorDashboardStep;
}

const Stepper = ({ model, t, activeStep }: IProps) => {
    const currentScreeningItem = screeningLinkMap(model.isFemale, model.isLiver).find((x) => x.step == model.currentRegistrationStep);
    const currentHistoryItem = historyLinkMap.find((x) => x.step == model.currentRegistrationStep);

    const startSection = t('DashLabel_StartSection', 'Start Section');

    const steps: Omit<IJourneyStep, 'isComplete'>[] = [
        {
            step: DonorDashboardStep.ScreeningQuestions,
            label: t('DashLabel_ScreeningQuestions', 'Screening Information'),
            link: currentScreeningItem ? currentScreeningItem.link : '',
            actionLabel: startSection,
        },
    ];

    if (model.canSelectCenter && model.canSelectPreferredCenter) {
        steps.push({
            step: DonorDashboardStep.SelectPreferredCenter,
            label: t('DashLabel_ChooseYourPreferredCenter', 'Choose a Donation Center').replaceAll(' ', ' <br/>'),
            link: '/register/select-center',
            actionLabel: startSection,
        });
    }

    steps.push({
        step: DonorDashboardStep.MedicalHistory,
        label: t('DashLabel_MedicalHistory', 'Medical History'),
        link: currentHistoryItem ? currentHistoryItem.link : '',
        actionLabel: startSection,
    });

    if (model.hasPwl) {
        steps.push({
            step: DonorDashboardStep.PreWorkupLabs,
            label: t('DashLabel_PreWorkupLabs', 'Pre-Workup Labs'),
            link: model.currentRegistrationStep == DashRegistrationStep.CystatinCLabs ? '/register/lab-orders' : '/register/pwl-jug/',
            actionLabel: startSection,
        });
    }

    if (model.canSelectCenter && model.useLegacyCenterSelect) {
        steps.push({
            step: DonorDashboardStep.ChooseYourCenter,
            label: t('DashLabel_ChooseYourPreferredCenter', 'Choose a Donation Center').replaceAll(' ', ' <br/>'),
            link: '/register/select-center',
            actionLabel: startSection,
        });
    }

    if (model.isFiveStar && model.isOptedInForSurveys) {
        steps.push({
            step: DonorDashboardStep.TransplantCenterWillContact,
            label: t('DashLabel_XplantCenterWillContactYou', 'Donation Center Will Contact You'),
            link: '/register/start-survey',
            actionLabel: t('DashLabel_ClickWhenContacted', 'I have been contacted'),
        });
        
        steps.push({
            step: DonorDashboardStep.DonorSatisfactionSurvey,
            label: t('DashLabel_DonorSatisfactionSurveys', 'Donor Satisfaction Surveys'),
            link: '/register/start-survey',
            actionLabel: t('DashLabel_ClickToContinue', 'click to continue'),
        });
    } else {
        steps.push({
            step: DonorDashboardStep.TransplantCenterWillContact,
            label: t('DashLabel_XplantCenterWillContactYou', 'Donation Center Will Contact You'),
            link: '',
            actionLabel: startSection,
        });
    }

    // A step is done once the journey has moved past its position. The active step is not always one
    // of the rendered steps, so when it is missing the enum order decides instead.
    const activeIndex = steps.findIndex((x) => x.step == activeStep);

    console.log(activeStep)

    const journeySteps: IJourneyStep[] = steps.map((x, index) => ({
        ...x,
        isComplete: activeIndex >= 0 ? index < activeIndex : activeStep > x.step,
    }));

    return (
        <div className='journey-steps'>
            {journeySteps.map((step, index) => (
                <Fragment key={step.step}>
                    {index > 0 && <div className='journey-connector'></div>}
                    <StepCircle step={step} number={index + 1} activeStep={activeStep} isLiver={model.isLiver} />
                </Fragment>
            ))}
        </div>
    );
};

export default Stepper;
